import type {CopilotAssignmentRepository} from '@github-ui/agent-sessions/components/copilot-assignment/copilot-assignment-types'
import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import {useCreateTask} from '@github-ui/agent-sessions/hooks/useCreateTask'
import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import {createContext, memo, use, useCallback, useMemo, useState} from 'react'

import {ItemType} from '../api/memex-items/item-type'
import {repositoryWithOwnerFromString} from '../models/repository'
import {useCreateMemexItem} from '../state-providers/memex-items/use-create-memex-item'
import {useRepositories} from '../state-providers/repositories/use-repositories'
import {Resources} from '../strings'
import useToasts, {ToastType} from './toasts/use-toasts'

type CreateJobResponse = {
  session_id: string
  job_id?: string
  updated_at?: string
  pull_request?: {
    id: number
    global_relay_id: string
    title: string
    body: string
  }
}

const MEMEX_EVENT_TYPE = 'memex-project'

type StartCopilotPRCreator = (problemStatement?: string, repository?: CopilotAssignmentRepository) => Promise<void>

type CopilotPRCreatorContextValue = {
  start: StartCopilotPRCreator
  prefetch: () => void
}

const CopilotPRCreatorContext = createContext<CopilotPRCreatorContextValue | null>(null)

export const useStartCopilotPRCreator = () => use(CopilotPRCreatorContext)

interface CopilotPRCreatorProps {
  onClose: () => void
  repository: CopilotAssignmentRepository
  problemStatement: string
  repositoryId?: number
}

const CopilotPRCreator = ({onClose, repository, problemStatement, repositoryId}: CopilotPRCreatorProps) => {
  const {addToast} = useToasts()
  const {createMemexItem} = useCreateMemexItem()
  const {createTaskAsync} = useCreateTask<CreateJobResponse>({
    onError: error => {
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: ToastType.error,
        message:
          error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
            ? error.message
            : 'Failed to create Copilot pull request',
      })
    },
  })

  const handleSubmit = useCallback(
    async (
      _graphQLId: string,
      selectedBranch?: string,
      content?: string,
      selectedAgent?: SelectedAgent,
      selectedModel?: string,
      selectedRepoNwo?: string,
      selectedRepoId?: number,
    ) => {
      const nwo = selectedRepoNwo ?? `${repository.owner.login}/${repository.name}`
      const repoId = selectedRepoId ?? repositoryId
      const statement = content || problemStatement

      try {
        const taskResult = await createTaskAsync({
          nwo,
          baseRef: selectedBranch,
          selectedAgent,
          problemStatement: statement,
          title: statement.slice(0, 100),
          eventType: MEMEX_EVENT_TYPE,
          modelId: selectedModel,
        })

        if (taskResult.pull_request?.id && repoId) {
          await createMemexItem({
            contentType: ItemType.PullRequest,
            content: {
              id: taskResult.pull_request.id,
              repositoryId: repoId,
            },
            memexProjectColumnValues: [],
            localColumnValues: [],
          })
        }

        onClose()
      } catch {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: ToastType.error,
          message: 'Failed to create Copilot pull request',
        })
      }
    },
    [repository, problemStatement, repositoryId, createTaskAsync, createMemexItem, onClose, addToast],
  )

  return (
    <CopilotAssignmentModal
      isOpen
      onClose={onClose}
      repository={repository}
      onSubmit={handleSubmit}
      title={Resources.createCopilotPRTitle}
      description=""
      promptPlaceholder={Resources.createCopilotPRPromptPlaceholder}
      promptLabelHidden
      promptRequired
    />
  )
}

export const CopilotPRCreatorProvider = memo(function CopilotPRCreatorProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [creatorProps, setCreatorProps] = useState<CopilotPRCreatorProps | undefined>()
  const {suggestRepositories} = useRepositories()

  const closeModal = useCallback(() => {
    setCreatorProps(undefined)
  }, [])

  const startCopilotPRCreator = useCallback<StartCopilotPRCreator>(
    async (problemStatement = '', repository?: CopilotAssignmentRepository) => {
      let repo = repository
      let repoId: number | undefined
      if (!repo) {
        // Get the first suggested repository if none provided
        const repos = await suggestRepositories()
        const firstRepo = repos.at(0)
        if (firstRepo?.nameWithOwner) {
          const parsed = repositoryWithOwnerFromString(firstRepo.nameWithOwner)
          if (parsed) {
            repo = {
              name: parsed.name,
              owner: {login: parsed.owner},
            }
            repoId = firstRepo.id
          }
        }
      }

      if (repo) {
        setCreatorProps({
          onClose: closeModal,
          repository: repo,
          problemStatement,
          repositoryId: repoId,
        })
      }
    },
    [suggestRepositories, closeModal],
  )

  const prefetch = useCallback(() => {
    // Prefetch repositories for faster modal opening
    void suggestRepositories()
  }, [suggestRepositories])

  const value: CopilotPRCreatorContextValue = useMemo(
    () => ({
      start: startCopilotPRCreator,
      prefetch,
    }),
    [startCopilotPRCreator, prefetch],
  )

  return (
    <CopilotPRCreatorContext value={value}>
      {children}
      {creatorProps && <CopilotPRCreator {...creatorProps} />}
    </CopilotPRCreatorContext>
  )
})

try{ CopilotPRCreatorContext.displayName ||= 'CopilotPRCreatorContext' } catch {}
try{ CopilotPRCreator.displayName ||= 'CopilotPRCreator' } catch {}
try{ CopilotPRCreatorProvider.displayName ||= 'CopilotPRCreatorProvider' } catch {}